import React, { useState } from "react";
import { Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import { logError } from "redux/actions";
import { ERROR_BOUNDARY } from "components/ErrorHandling/ErrorConstants";

const hasHASelector = (state) => state._hasHardwareAcceleration;
const selectedSessionIDSelector = (state) => _.get(state, ["playlist", "data", "routeID"], null);

const savedStates = new Map();

function getErrorMetadata(selectedSessionID, id) {
    // Log the error with detailed information to measure how often this occurs and to help with debugging
    const sessionIdForLog = selectedSessionID || "No session selected";
    const uuid = uuidv4();
    const stack = new Error().stack;

    const errorMetadata = {
        message: "Hardware acceleration is not enabled",
        stack: stack,
        componentStack: null,
        url: window.location.href,
        selectedSessionID: sessionIdForLog,
        uuid: uuid,
        errorType: ERROR_BOUNDARY,
        frontendVersion: process.env["REACT_APP_GIT_VERSION"],
    };

    return errorMetadata;
}

export const HardwareAccelerationChecker = ({ required, silent = false, children, errorChildren = null, errorMessage = "", id = "hardware-acceleration" }) => {
    const dispatch = useDispatch();
    const hasHA = useSelector(hasHASelector);
    const selectedSessionID = useSelector(selectedSessionIDSelector);
    const [checked, setChecked] = useState(false);

    if (hasHA) {
        return children;
    }

    if (!checked && !hasHA && !savedStates.has(id)) {
        console.warn(`Hardware acceleration is not enabled. Some features may not work optimally. ID: ${id}`);
        if (silent) {
            return null;
        }

        // Log the error with detailed information to measure how often this occurs and to help with debugging
        const errorMetadata = getErrorMetadata(selectedSessionID, id);

        if (dispatch && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
            dispatch(logError(errorMetadata));
        }

        if (required) {
            return (
                <Modal
                    title="Hardware Acceleration Required"
                    okText="Ok"
                    visible={true}
                    onOk={() => {
                        setChecked(true);
                        savedStates.set(id, { checked: true, renderForced: false });
                    }}
                    cancelButtonProps={{ style: { display: "none" } }}
                    onCancel={() => {
                        setChecked(true);
                    }}>
                    <>
                        {errorMessage ||
                            "Your system does not have hardware acceleration enabled. Please enable it in your system settings to use this application effectively."}
                    </>
                </Modal>
            );
        } else {
            return (
                <Modal
                    title="Hardware Acceleration Not Detected"
                    visible={true}
                    okText="Continue Anyway"
                    cancelText="Close"
                    onOk={() => {
                        setChecked(true);
                        savedStates.set(id, { checked: true, renderForced: true });
                    }}
                    onCancel={() => {
                        setChecked(true);
                        savedStates.set(id, { checked: true, renderForced: false });
                    }}>
                    <>{errorMessage || "Your system does not have hardware acceleration enabled. Some features may not work optimally."}</>
                </Modal>
            );
        }
    }

    if (!hasHA && ((savedStates.has(id) && savedStates.get(id).checked) || checked)) {
        if (required) {
            return errorChildren || null;
        }

        if (savedStates.get(id).renderForced) {
            return children;
        } else {
            return errorChildren || null;
        }
    }

    return null;
};

export function supportsHardwareAcceleration() {
    // create a test function for both "default" drawing and forced software
    const test = (force = false) => {
        // Firefox (at least on macOS) doesn't accelerate OffscreenCanvas
        const canvas = document.createElement("canvas");
        // willReadFrequently will force software rendering
        const ctx = canvas.getContext("2d", { willReadFrequently: force });
        ctx.moveTo(0, 0);
        ctx.lineTo(120, 121); // HWA is bad at obliques
        ctx.stroke();
        return ctx.getImageData(0, 0, 200, 200).data.join();
    };

    return test(true) !== test(false);
}

export const withHardwareAcceleration = (hasHA, selectedSessionID = null, dispatch = null, fnToRun, silent = false, id = "with-hardware-acceleration") => {
    if (!hasHA) {
        console.warn(`Hardware acceleration is not enabled. Some features may not work optimally. ID: ${id}`);
        if (silent) {
            return;
        }

        // Log the error with detailed information to measure how often this occurs and to help with debugging
        const errorMetadata = getErrorMetadata(selectedSessionID, id);

        if (dispatch && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
            dispatch(logError(errorMetadata));
        }

        Modal.info({
            title: "Hardware Acceleration Disabled",
            content: (
                <div>
                    <p>Your browser does not have Hardware Acceleration enabled.</p>
                    <p>
                        To use this feature, <strong>enable</strong> Hardware Acceleration in your browser settings.
                    </p>
                </div>
            ),
        });
        return;
    }

    fnToRun();
};
